import { memo, useMemo } from "react";

import { FeatureIslandContainer, FeatureIslandMain } from "@/components/features/_layouts";

import {
	AccordionLayout,
	DiscountCodeFormFieldComposition,
	DiscountDescriptionFormFieldComposition,
	DiscountEditHeaderComposition,
	DiscountNameFormFieldComposition,
	FeatureIslandLayout,
	IsUncancelableFormFieldComposition,
	Layout,
	MultiFormFieldComposition,
	PriceTotalLimitAmountFormFieldComposition,
	SaleTypeForPlanFormFieldComposition,
	SaleTypeForTermFormFieldComposition,
	StayableDayOfTheWeekFormFieldComposition,
} from "../../../_shared";
import { useInitialized } from "../model";

type Props = {
	hotelID: string;
	discountID: string;
};

export const DiscountMultiEditPage = memo(({ hotelID, discountID }: Props) => {
	const {
		//
		queryData,
		viewModel,

		// mutation
		onUpdateDiscountMulti,

		//
		// 公開にする
		onPublish,
		// 非公開にする
		onUnpublish,

		// 基本設定
		// ディスカウント名の変更
		onChangeName,
		// ディスカウント説明の変更
		onChangeDescription,
		// ディスカウントコードの変更
		onChangeDiscountCode,

		// 適用ディスカウント
		// 適用ディスカウントの追加
		onAddMultiDiscounts,
		// 適用ディスカウントの変更
		onChangeMultiDiscounts,
		// 適用ディスカウントの削除
		onDeleteMultiDiscounts,

		// 追加設定
		// ディスカウント上限金額を設定しない
		onUnSettingDiscountPriceTotalLimit,
		// ディスカウント上限金額の変更
		onChangeDiscountPriceTotalLimit,
		// ディスカウントをプラン限定にするかの変更
		onChangeSaleTypeForPlan,
		// キャンセル不可のディスカウントにするかの変更
		onChangeIsUncancelable,
		// ディスカウントの利用可能期間を設定するかの変更
		onChangeSaleTypeForTerm,
		// 公開期間を変更
		onChangeSale,
		// 利用可能期間を変更
		onChangeStayable,
		// 利用可能曜日の変更
		onChangeStayableDayOfTheWeek,
	} = useInitialized(hotelID, discountID);

	/**
	 * 通常・連泊
	 */
	const ordinaryAndLongTermDiscounts = useMemo(() => {
		const discountOrdinary = queryData?.discountOrdinary ?? [];
		const discountLongTerm = queryData?.discountLongTerm ?? [];
		return [...discountOrdinary, ...discountLongTerm];
	}, [queryData.discountOrdinary, queryData.discountLongTerm]);

	/**
	 * @todo
	 * ViewModel でエラーを保持しないようにしている
	 * 適用ディスカウント、利用可能な曜日を ViewModel で管理していないため、ViewModel が保持しているエラーと統合する
	 */
	const hasError = useMemo(() => {
		return (
			viewModel.hasError || viewModel.multiDiscounts.length === 0 || viewModel.stayableDayOfTheWeek?.length === 0
		);
	}, [viewModel.hasError, viewModel.multiDiscounts.length, viewModel.stayableDayOfTheWeek?.length]);

	return (
		<FeatureIslandContainer>
			<DiscountEditHeaderComposition
				title="複合ディスカウントの設定"
				inSale={viewModel.inSale}
				isDirty={viewModel.isDirty}
				hasError={hasError}
				onPublish={onPublish}
				onUnpublish={onUnpublish}
				onSave={onUpdateDiscountMulti}
			/>
			<FeatureIslandMain>
				<Layout className="mx-auto grid max-w-screen-lg gap-y-12">
					<FeatureIslandLayout title="基本設定">
						<Layout className="divide-y divide-zinc-100 [&>div]:p-3">
							{/* ディスカウントの名前 */}
							<DiscountNameFormFieldComposition
								discountName={viewModel.name}
								//
								onChangeName={onChangeName}
							/>

							{/* ディスカウントの説明 */}
							<DiscountDescriptionFormFieldComposition
								discountDescription={viewModel.description}
								//
								onChangeDescription={onChangeDescription}
							/>

							{/* ディスカウントコード */}
							<DiscountCodeFormFieldComposition
								discountCode={viewModel.discountCode}
								//
								onChangeCode={onChangeDiscountCode}
							/>
						</Layout>
					</FeatureIslandLayout>

					<FeatureIslandLayout title="適用ディスカウント設定">
						<MultiFormFieldComposition
							ordinaryAndLongTermDiscounts={ordinaryAndLongTermDiscounts}
							multiDiscounts={viewModel.multiDiscounts}
							onAddMultiDiscounts={onAddMultiDiscounts}
							onChangeMultiDiscounts={onChangeMultiDiscounts}
							onDeleteMultiDiscounts={onDeleteMultiDiscounts}
						/>
					</FeatureIslandLayout>

					<FeatureIslandLayout title="追加設定">
						{/* 合計上限価格の設定 */}
						<AccordionLayout
							title="ディスカウント合計上限金額に関する設定"
							id="discount-price-total-limit-settings"
						>
							<PriceTotalLimitAmountFormFieldComposition
								discountPriceTotalLimitAmount={viewModel.discountPriceTotalLimit?.amount}
								onUnSettingDiscountPriceTotalLimit={onUnSettingDiscountPriceTotalLimit}
								onChangeDiscountPriceTotalLimit={onChangeDiscountPriceTotalLimit}
							/>
						</AccordionLayout>

						{/* プラン限定の設定 */}
						<AccordionLayout
							title="プラン限定のディスカウントに関する設定"
							id="discount-sale-type-for-plan-settings"
						>
							<SaleTypeForPlanFormFieldComposition
								saleTypeForPlan={viewModel.saleTypeForPlan}
								onChangeSaleTypeForPlan={onChangeSaleTypeForPlan}
							/>
						</AccordionLayout>

						{/* キャンセル不可のディスカウントにするかの設定 */}
						<AccordionLayout
							title="キャンセル不可のディスカウントに関する設定"
							id="discount-is-uncancelable"
						>
							<IsUncancelableFormFieldComposition
								isUncancelable={viewModel.isUncancelable}
								onChangeIsUncancelable={onChangeIsUncancelable}
							/>
						</AccordionLayout>

						{/* 利用可能な期間の設定 */}
						<AccordionLayout title="利用可能な期間に関する設定" id="discount-sale-type-for-term-settings">
							<SaleTypeForTermFormFieldComposition
								saleTypeForTerm={viewModel.saleTypeForTerm}
								saleStart={viewModel.saleStart ?? undefined}
								saleEnd={viewModel.saleEnd ?? undefined}
								stayableStart={viewModel.stayableStart ?? undefined}
								stayableEnd={viewModel.stayableEnd ?? undefined}
								onChangeSaleTypeForTerm={onChangeSaleTypeForTerm}
								onChangeSale={onChangeSale}
								onChangeStayable={onChangeStayable}
							/>
						</AccordionLayout>

						{/* 利用可能な曜日の設定 */}
						<AccordionLayout
							title="利用可能な曜日に関する設定"
							id="discount-stayable-day-of-the-week-settings"
						>
							<StayableDayOfTheWeekFormFieldComposition
								stayableDayOfTheWeek={viewModel.stayableDayOfTheWeek ?? []}
								onChangeStayableDayOfTheWeek={onChangeStayableDayOfTheWeek}
							/>
						</AccordionLayout>
					</FeatureIslandLayout>
				</Layout>
			</FeatureIslandMain>
		</FeatureIslandContainer>
	);
});
